import type { FC } from 'react';
import { memo } from 'react';
import { Divider } from 'src/common/components/divider';
import { Icon } from 'src/common/components/icon';
import { publicLink } from 'src/common/const';
import { ID_TOC_CONTAINER } from 'src/hooks/page/use-dnd/helper';
import { useIsGuest } from 'src/hooks/share/use-permission-utils';
import { useCurrentSpace } from 'src/hooks/space';
import { useTocScroll } from 'src/hooks/toc/toc-scroll';
import type { NextBlock } from 'src/redux/types';
import { useObservableStore } from 'src/services/rxjs-redux/hook';
import { StoreContextProvider } from 'src/services/store-context/provider';
import { bizTracker } from 'src/utils/biz-tracker';
import { useGetPageId } from 'src/utils/getPageId';
import { AsideFooter, AsideFooterToolList } from './aside-footer';
import { AsideHeader } from './aside-header';
import { AsideContext } from './context';
import { CreateSpace } from './create-space';
import { QuickSettings } from './quick-setting';
import { ShareAsideContext } from './share/share-aside-context';
import { ShareAsideHeader } from './share/share-aside-header';
import { ShareToc } from './share/share-toc';
import { SubscribeAsideHeader } from './subscribe/subscribe-aside-header';
import { SubscribeToc } from './subscribe/subscribe-toc';
import { Toc } from './toc';
import { AiToc } from './toc/ai-toc';

const tocId = 'aside-toc';

export const Aside: FC = memo(() => {
  const isGuest = useIsGuest();
  const currentSpace = useCurrentSpace();
  const pageId = useGetPageId();

  useTocScroll({
    containerId: ID_TOC_CONTAINER,
    tocId,
    toId: pageId,
  });

  return (
    <>
      <StoreContextProvider wait={500} waitMode="debounce">
        <AsideContext.Provider value={true}>
          <div id={tocId} className="relative flex flex-col w-full h-full">
            <AsideHeader />
            <QuickSettings />
            <div className="h-2 flex-shrink-0" />
            {!__PRIVATE__ && <AiToc />}
            <Toc>
              <AsideFooterToolList />
            </Toc>
            {currentSpace.uuid && (isGuest ? <CreateSpace /> : <AsideFooter />)}
          </div>
        </AsideContext.Provider>
      </StoreContextProvider>
    </>
  );
});

// 分享页的header
export const ShareAside: FC = memo(() => {
  const pageId = useGetPageId();
  const rootId = useObservableStore(
    (state) => {
      let block: NextBlock | undefined = state.blocks[pageId];
      let result = block;
      while (block) {
        const parent = state.blocks[block.parentId];
        if (parent?.permissions.some((p) => p.shareShowSidebar)) {
          result = parent;
        }
        // @ts-ignore ignore space block
        if (parent && parent.type !== 'SPACE') {
          block = parent;
          continue;
        }
        break;
      }
      return result?.uuid;
    },
    [pageId]
  );

  useTocScroll({
    containerId: ID_TOC_CONTAINER,
    tocId,
    toId: pageId,
  });

  if (!rootId) return null;
  return (
    <ShareAsideContext.Provider value={rootId}>
      <div id={tocId} className="relative flex flex-col w-full h-full">
        <ShareAsideHeader />
        <Divider className="mx-4 mt-5" />
        <ShareToc />
      </div>
    </ShareAsideContext.Provider>
  );
});

// 订阅页的header
export const SubscribeAside: FC = memo(() => {
  const pageId = useGetPageId();
  useTocScroll({
    containerId: ID_TOC_CONTAINER,
    tocId,
    toId: pageId,
  });
  return (
    <div id={tocId} className="relative flex flex-col w-full h-full">
      <SubscribeAsideHeader />
      <SubscribeToc />
      {!__BUILD_IN__ && (
        <a
          href={publicLink.subscribeCenter}
          target="_blank"
          className="border-t mt-1 justify-self-end py-3 animate-hover rounded-none animate-click flex items-center px-4 space-x-2 text-grey3"
          onClick={() => {
            bizTracker.event('knowledge', { is_from: 'down_to_knowledge' });
          }}
        >
          <Icon name={'IcKnowledgeBase'} size="middle" />
          <div className="text-t3-medium">知识库</div>
        </a>
      )}
    </div>
  );
});
